import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import { toast } from "sonner";

import { VISAVALUE } from "../types/visa.type";
import {
  DefaultTemplate,
  DefaultTemplateStatus
} from "../api/types/default-template";
import { DefaultTemplatesAPI } from "../api/default-templates";
import { DraftType } from "../api/types/draft";

interface DefaultTemplatesContextType {
  templates: DefaultTemplate[];
  isLoading: boolean;
  isInitialLoading: boolean;
  error: Error | null;
  selectedTemplate: DefaultTemplate | null;
  fetchTemplates: (params?: {
    status?: DefaultTemplateStatus;
    visa_type?: VISAVALUE;
    draft_type?: DraftType;
    visa_subtype?: string;
  }) => Promise<void>;
  selectTemplate: (templateId: string) => Promise<void>;
  clearSelectedTemplate: () => void;
  updateTemplateStatus: (
    templateId: string,
    status: DefaultTemplateStatus
  ) => Promise<void>;
}

const DefaultTemplatesContext =
  createContext<DefaultTemplatesContextType | null>(null);

export const useDefaultTemplates = () => {
  const context = useContext(DefaultTemplatesContext);
  if (!context) {
    throw new Error(
      "useDefaultTemplates must be used within a DefaultTemplatesProvider"
    );
  }
  return context;
};

export const DefaultTemplatesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [templates, setTemplates] = useState<DefaultTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selectedTemplate, setSelectedTemplate] =
    useState<DefaultTemplate | null>(null);

  const fetchTemplates = useCallback(
    async (params?: {
      status?: DefaultTemplateStatus;
      visa_type?: VISAVALUE;
      draft_type?: DraftType;
      visa_subtype?: string;
    }) => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await DefaultTemplatesAPI.getAll(params);
        setTemplates(data);
      } catch (err) {
        setError(err as Error);
        toast.error("Error fetching default templates", {
          description: (err as Error).message,
          duration: 5000
        });
      } finally {
        setIsLoading(false);
        setIsInitialLoading(false);
      }
    },
    []
  );

  const selectTemplate = useCallback(async (templateId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const template = await DefaultTemplatesAPI.getById(templateId);
      setSelectedTemplate(template);
    } catch (err) {
      setError(err as Error);
      toast.error("Error fetching template details", {
        description: (err as Error).message,
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearSelectedTemplate = useCallback(() => {
    setSelectedTemplate(null);
  }, []);

  const updateTemplateStatus = useCallback(
    async (templateId: string, status: DefaultTemplateStatus) => {
      await DefaultTemplatesAPI.updateStatus(templateId, status);
      await fetchTemplates({});
    },
    [fetchTemplates]
  );

  const value = useMemo(
    () => ({
      templates,
      isLoading,
      isInitialLoading,
      error,
      selectedTemplate,
      fetchTemplates,
      selectTemplate,
      clearSelectedTemplate,
      updateTemplateStatus
    }),
    [
      templates,
      isLoading,
      isInitialLoading,
      error,
      selectedTemplate,
      fetchTemplates,
      selectTemplate,
      clearSelectedTemplate,
      updateTemplateStatus
    ]
  );

  return (
    <DefaultTemplatesContext.Provider value={value}>
      {children}
    </DefaultTemplatesContext.Provider>
  );
};
